// Phase-A R5 — Redesigned LoginPage.
// Visual design from docs/my-upload/login/LoginPage.tsx (owner template).
// Wired to the existing auth flow (auth.login, apiRoleToLocal,
// role-aware redirect, error toasts) — no behaviour regression vs the
// pre-R5 LoginPage in Auth.tsx.
//
// Demo quick-login by role: an 11-chip grid (all roles) shown only on the
// demo tenant. Clicking a chip fills that role's real seeded demo account, so
// login actually lands in that role's workspace — functional, not cosmetic.
// The post-login role still comes from the authenticated account server-side
// (server `roles` → apiRoleToLocal → redirect), never from the chip. The rest
// of the R5 login layout/theme is unchanged.
import React from "react";
import { useRole, ROLES, type RoleId } from "../../role";
import { apiRoleToLocal } from "../../auth/role-map";
import { isRetiredRole } from "../../config/adapt-flags";
import { useAuth, isTwoFactorChallenge } from "../../auth/AuthContext";
import { ApiError } from "../../api/client.js";
import { formatLockCountdown, isLockActive, lockRemainingSeconds } from "../../auth/lockout-countdown";
import type { Go } from "../../router";
import { BrandPanel } from "./BrandPanel";
import { CoBrandFooter } from "./CoBrandFooter";
import {
  Icon,
  Field,
  TextInput,
  Checkbox,
  Eyebrow,
  PillButton,
  Wordmark,
  Spinner,
  PassStrength,
  SsoButton,
  GoogleGlyph,
  NationalGlyph,
  OrgGlyph,
  useR5Toast,
} from "./login-atoms";

// ---- Demo accounts (one per role) ----------------------------------------
// The role chips are a DEMO QUICK-LOGIN, NOT an auth constraint: the
// post-login role always comes from the authenticated account (server
// `roles` → apiRoleToLocal). Each chip fills that role's real seeded account
// EMAIL only — the password is deliberately NOT shipped to the client (it would
// otherwise be readable in the public bundle, defeating the point of rotating
// it). The operator types the secret (held only on the server / by the owner).
// Shown only on the demo tenant (that's where these accounts live).

const EMAIL_LABEL = "ایمیل یا نام کاربری";
const EMAIL_PLACEHOLDER = "you@example.com";

interface DemoAccount {
  roleId: RoleId;
  label: string;
  email: string;
}

// S-NRI-1 (دستور صریح مالک، 2026-07-22): the role chips are ALWAYS shown on
// the production `nri` tenant — clicking a chip fills that role's account
// EMAIL only; the password is typed by the operator (it exists ONLY in the
// server .env — zero passwords ever ship in this bundle, which is the actual
// بلوک ۵.۴ security requirement). The old VITE_DEMO_MODE build-time gate was
// removed for the chips; only emails ship, and they are not secrets.

// Ordered by hierarchy for the chip grid: academic actors → leadership → staff/ops
// → oversight → external. Every email is a real seeded account on the `nri` tenant.
const DEMO_ACCOUNTS: DemoAccount[] = [
  // — academic —
  { roleId: "student", label: "کارمند/فراگیر", email: "student1@nriuniversity.ir" },
  { roleId: "instructor", label: "مدرس", email: "instructor1@nriuniversity.ir" },
  { roleId: "ta", label: "دستیار آموزشی", email: "ta1@nriuniversity.ir" },
  // — leadership —
  { roleId: "faculty_admin", label: "ریاست دانشکده", email: "facadmin1@nriuniversity.ir" },
  { roleId: "admin", label: "مدیر ارشد سامانه", email: "admin@nriuniversity.ir" },
  { roleId: "super_admin", label: "راهبر سامانه", email: "superadmin@nriuniversity.ir" },
  // — staff / ops —
  { roleId: "content_manager", label: "مدیر محتوا", email: "cm1@nriuniversity.ir" },
  // S-NRI-ADAPT — «سازندهٔ پکیج» (استودیو دانش).
  { roleId: "creator", label: "سازندهٔ پکیج", email: "creator1@nriuniversity.ir" },
  // R-COMPLY — مدیر مالی owns the finance workspace; admin intentionally does NOT.
  { roleId: "finance_manager", label: "مدیر مالی", email: "finance1@nriuniversity.ir" },
  { roleId: "support", label: "پشتیبانی", email: "support1@nriuniversity.ir" },
  // — oversight —
  { roleId: "moderator", label: "ناظر محتوا/انجمن", email: "moderator1@nriuniversity.ir" },
  // WELL-4 — مشاور (least-privilege wellness desk).
  { roleId: "counselor", label: "مشاور سلامت", email: "counselor1@nriuniversity.ir" },
  // — external —
  { roleId: "parent", label: "والد", email: "parent1@nriuniversity.ir" },
  { roleId: "org", label: "رابط سازمان", email: "org1@nriuniversity.ir" },
  // — سازمانی / وزارت (S-NRI-3): the org-tree governance roles. Each fills the
  // seeded account EMAIL only (password typed by the operator, never shipped).
  { roleId: "holding_manager", label: "مدیر هلدینگ", email: "holding1@nriuniversity.ir" },
  { roleId: "company_admin", label: "ادمین شرکت", email: "coadmin1@nriuniversity.ir" },
  { roleId: "company_training_manager", label: "مدیر آموزش شرکت", email: "cotrain1@nriuniversity.ir" },
  { roleId: "certification_officer", label: "مسئول صدور گواهی", email: "certoff1@nriuniversity.ir" },
  { roleId: "practical_assessor", label: "ارزیاب عملی", email: "assessor1@nriuniversity.ir" },
  { roleId: "ministry_supervisor", label: "ناظر وزارت", email: "supervisor1@nriuniversity.ir" },
  { roleId: "auditor", label: "حسابرس", email: "auditor1@nriuniversity.ir" },
];

// ---- Demo role quick-login (chip grid) -----------------------------------

interface DemoRolePickerProps {
  selected: RoleId | null;
  onPick: (acct: DemoAccount) => void;
}

const DemoRolePicker: React.FC<DemoRolePickerProps> = ({ selected, onPick }) => (
  <div
    style={{
      display: "grid",
      gap: 8,
      padding: 14,
      background: "var(--r5-bg-2)",
      border: "1px dashed var(--r5-line-strong)",
      borderRadius: 14,
    }}
  >
    <div
      style={{
        fontSize: 12,
        color: "var(--r5-muted)",
        fontFamily: "var(--r5-mono)",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
      }}
    >
      ورود سریع با حساب نمونه
    </div>
    <div role="group" aria-label="حساب‌های نمونه بر اساس نقش" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {DEMO_ACCOUNTS.filter((a) => !isRetiredRole(a.roleId)).map((a) => {
        const active = a.roleId === selected;
        return (
          <button
            key={a.roleId}
            type="button"
            aria-pressed={active}
            onClick={() => onPick(a)}
            style={{
              padding: "7px 13px",
              borderRadius: 999,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              background: active ? "var(--r5-sage-deep)" : "var(--r5-paper)",
              color: active ? "#fff" : "var(--r5-ink-2)",
              border: "1px solid " + (active ? "var(--r5-sage-deep)" : "var(--r5-line-strong)"),
              transition: "all 160ms ease",
            }}
          >
            {a.label}
          </button>
        );
      })}
    </div>
    <div style={{ fontSize: 11, color: "var(--r5-muted)", lineHeight: 1.7 }}>
      با کلیک روی هر نقش، فقط ایمیل آن حساب پر می‌شود؛ سپس رمز عبور را وارد کرده و «ورود به حساب» را بزنید.
    </div>
  </div>
);

// ---- Main page ------------------------------------------------------------

interface AuthPageProps { go: Go }

interface LoginErrors {
  tenantSlug?: string;
  email?: string;
  password?: string;
  general?: string;
}

// R7.9 — apiRoleToLocal moved to apps/web/src/auth/role-map.ts so
// LoginPage and the legacy Auth.tsx share a single source of truth.
// The previous local copy mapped only 3 of 10 API roles, silently
// bucketing parent/org/ta/cm/support/moderator/super_admin as
// "student" — that bug caused Gate A §5 + OWNER-FINDING-2.

export const LoginPage: React.FC<AuthPageProps> = ({ go }) => {
  const { setRole } = useRole();
  const auth = useAuth();
  const [tenantSlug, setTenantSlug] = React.useState<string>("nri");
  const [email, setEmail] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>("");
  const [showPass, setShowPass] = React.useState<boolean>(false);
  const [remember, setRemember] = React.useState<boolean>(true);
  const [twofa, setTwofa] = React.useState<boolean>(true);
  const [errors, setErrors] = React.useState<LoginErrors>({});
  const [pending, setPending] = React.useState<boolean>(false);
  // R-AUTH-5 — 2-step login: when the account has 2FA, step 1 returns a pending
  // token and we switch to the code step instead of going home.
  const [twoFaStage, setTwoFaStage] = React.useState<boolean>(false);
  const [pendingToken, setPendingToken] = React.useState<string>("");
  const [twoFaCode, setTwoFaCode] = React.useState<string>("");
  // Demo quick-login: which role chip is currently filled (highlight only).
  const [selectedRole, setSelectedRole] = React.useState<RoleId | null>(null);
  const { node: toastNode, show: showToast } = useR5Toast();

  // S-2/S-3 wave 2 — per-account lockout countdown. When login returns 429
  // `account_locked`, hold the lock-until timestamp so the submit button disables
  // and a persistent countdown replaces the toast (which would vanish). Ticks
  // every second, self-clears when the window elapses, and is reset when the user
  // edits the account fields (switching account). Inert unless the backend flag is
  // on (it never emits account_locked otherwise) → zero effect on today's flow.
  const [lockUntil, setLockUntil] = React.useState<number | null>(null);
  const [nowTs, setNowTs] = React.useState<number>(() => Date.now());
  React.useEffect(() => {
    if (lockUntil == null) return;
    const id = window.setInterval(() => setNowTs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [lockUntil]);
  const lockRemaining = lockRemainingSeconds(lockUntil, nowTs);
  const isLocked = isLockActive(lockUntil, nowTs);
  React.useEffect(() => {
    if (lockUntil != null && lockRemaining <= 0) setLockUntil(null);
  }, [lockUntil, lockRemaining]);

  const finishLogin = (roles: string[]): void => {
    const localRole = apiRoleToLocal(roles);
    setRole(localRole);
    showToast("با موفقیت وارد شدید — در حال انتقال…");
    // S-9 — if the user came here from a plan CTA (guest/logged-out), resume the
    // checkout they picked instead of dumping them on their role home page.
    let pendingPlan: string | null = null;
    try {
      pendingPlan = sessionStorage.getItem("s9_checkout_plan");
      if (pendingPlan) sessionStorage.removeItem("s9_checkout_plan");
    } catch {
      /* sessionStorage unavailable */
    }
    if (pendingPlan) {
      go("checkout", pendingPlan);
      return;
    }
    go(ROLES[localRole]?.homeRoute || "dashboard");
  };

  const pickDemo = (acct: DemoAccount): void => {
    // Fill the EMAIL only — the password is NEVER in the client bundle/seed. Each
    // demo role now has its OWN password, so clear the field on a role switch:
    // the operator always types the password for the role they just picked.
    setEmail(acct.email);
    setPassword("");
    setSelectedRole(acct.roleId);
    // A programmatic setEmail doesn't fire the input's onChange, so clear the lock
    // countdown here too — otherwise a stale lock on the PREVIOUS account would keep
    // submit disabled after switching to a different (unlocked) demo role.
    setLockUntil(null);
    showToast(`ایمیل «${acct.label}» پر شد — رمز عبور را وارد کرده و «ورود» را بزنید`);
  };

  const validate = (): LoginErrors => {
    const e: LoginErrors = {};
    if (!tenantSlug.trim()) e.tenantSlug = "شناسه سازمان الزامی است.";
    if (!email.trim()) e.email = "ایمیل/کد کاربری الزامی است.";
    else if (email.includes("@") && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = "فرمت ایمیل صحیح نیست.";
    if (!password) e.password = "رمز عبور الزامی است.";
    else if (password.length < 8) e.password = "رمز عبور حداقل ۸ کاراکتر باشد.";
    return e;
  };

  const handleLogin = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) {
      showToast(Object.values(errs)[0] ?? "ورودی نامعتبر است.");
      return;
    }
    setPending(true);
    try {
      const result = await auth.login({
        tenantSlug: tenantSlug.trim().toLowerCase(),
        email: email.trim().toLowerCase(),
        password,
      });
      // R-AUTH-5: 2FA-enabled account → switch to the code step.
      if (isTwoFactorChallenge(result)) {
        setPendingToken(result.pendingToken);
        setTwoFaStage(true);
        setErrors({});
        showToast("رمز درست است — کد تأیید دومرحله‌ای را وارد کنید.");
        return;
      }
      finishLogin(result.roles);
    } catch (err: unknown) {
      let msg = "خطای ناشناخته";
      if (err instanceof ApiError) {
        msg = (err as { displayMessage?: string }).displayMessage ?? msg;
        // S-2/S-3 wave 2 — a 429 account_locked carries retryAfterSeconds; start a
        // persistent countdown + disable submit until the window elapses.
        const body = (err as { body?: { code?: string; retryAfterSeconds?: number } }).body;
        if (body?.code === "account_locked" && typeof body.retryAfterSeconds === "number") {
          // Seed nowTs fresh: the tick interval only runs while lockUntil != null, so
          // before the first lock nowTs is frozen at mount — without this the first
          // countdown render would be inflated by the seconds-since-mount until the
          // interval's first tick ~1s later.
          const t = Date.now();
          setNowTs(t);
          setLockUntil(t + body.retryAfterSeconds * 1000);
        }
      } else if (err instanceof Error) {
        msg = err.message;
      }
      setErrors({ general: msg });
      showToast(msg);
    } finally {
      setPending(false);
    }
  };

  // R-AUTH-5 — step 2: exchange the pending token + code for a session.
  const handleTwoFactor = async (): Promise<void> => {
    if (twoFaCode.trim().length < 6) {
      setErrors({ general: "کد تأیید را کامل وارد کنید." });
      return;
    }
    setPending(true);
    try {
      const user = await auth.loginTwoFactor({ pendingToken, code: twoFaCode.trim() });
      finishLogin(user.roles);
    } catch (err: unknown) {
      let msg = "کد تأیید نادرست است.";
      if (err instanceof ApiError) {
        msg = (err as { displayMessage?: string }).displayMessage ?? msg;
      } else if (err instanceof Error) {
        msg = err.message;
      }
      setErrors({ general: msg });
      showToast(msg);
    } finally {
      setPending(false);
    }
  };

  const cancelTwoFactor = (): void => {
    setTwoFaStage(false);
    setTwoFaCode("");
    setPendingToken("");
    setErrors({});
  };

  return (
    <main
      className="r5-login-shell"
      data-screen-label="01 Login"
      style={{
        minHeight: "100dvh",
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 1fr)",
        background: "var(--r5-bg)",
        color: "var(--r5-ink)",
      }}
    >
      {/* Brand panel — hidden by CSS at <=720px */}
      <BrandPanel showTestimonial />

      {/* Form panel */}
      <section
        className="r5-form-panel"
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          padding: "clamp(20px, 4vw, 48px) clamp(20px, 5vw, 72px)",
          minHeight: "100dvh",
          background: "var(--r5-bg)",
        }}
      >
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "clamp(20px, 4vh, 48px)",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <Wordmark />
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <PillButton onClick={() => showToast("جستجو در حالت نمونه فعال نیست")} ariaLabel="جستجو">
              <Icon d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm10 2-4.35-4.35" size={16} />
            </PillButton>
            {/* Theme toggle removed (D-2026-06-13): dark mode disabled for now. */}
            {/* R7.3 B.4 — was `ariaLabel="تغییر زبان"` with visible
                text "FA". Lighthouse's label-content-name-mismatch rule
                requires the visible text to be part of the accessible
                name. Including "FA" in the aria-label resolves it while
                preserving the Persian context. */}
            <PillButton onClick={() => showToast("زبان نمایش: فارسی")} ariaLabel="تغییر زبان (FA)">
              <span style={{ fontSize: 12, fontWeight: 600 }}>FA</span>
            </PillButton>
          </div>
        </header>

        <div className="r5-form-center" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div
            style={{
              width: "100%",
              maxWidth: 460,
              animation: "r5-card-in 700ms cubic-bezier(.22,1,.36,1) both",
            }}
          >
            <Eyebrow text="WELCOME BACK" subtext="ورود" />
            <h1
              style={{
                fontSize: "clamp(34px, 4.4vw, 52px)",
                lineHeight: 1.05,
                margin: "10px 0 14px",
                fontWeight: 700,
                letterSpacing: "-0.01em",
                color: "var(--r5-ink)",
              }}
            >
              خوش آمدید
            </h1>
            <p style={{ color: "var(--r5-muted)", margin: 0, fontSize: 15, lineHeight: 1.65 }}>
              با حساب خود وارد شوید. اگر هنوز ثبت‌نام نکرده‌اید، می‌توانید در کمتر از{" "}
              <b style={{ color: "var(--r5-ink-2)" }}>۲ دقیقه</b> شروع کنید.
            </p>

            {/* R-AUTH-5 — 2FA code step (shown after a correct password when the
                account has 2FA). The R5 login shell + brand panel are
                unchanged; only the form body swaps to this focused view. */}
            {twoFaStage ? (
            <form
              onSubmit={(e) => { e.preventDefault(); void handleTwoFactor(); }}
              style={{ marginTop: 26, display: "grid", gap: 16 }}
              noValidate
            >
              <div style={{ fontSize: 13, color: "var(--r5-ink-2)", lineHeight: 1.8 }}>
                تأیید دومرحله‌ای برای این حساب فعال است. کد ۶ رقمی اپ Authenticator
                (یا یکی از کدهای بازیابی) را وارد کنید.
              </div>
              <Field label="کد تأیید دومرحله‌ای" required error={errors.general}>
                <TextInput
                  name="twofa-code"
                  type="text"
                  value={twoFaCode}
                  onChange={(e) => setTwoFaCode(e.target.value)}
                  placeholder="۱۲۳۴۵۶"
                  dir="ltr"
                  icon="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z"
                />
              </Field>
              <button
                type="submit"
                disabled={pending}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  padding: "14px 18px", borderRadius: 14, border: "none",
                  cursor: pending ? "default" : "pointer",
                  background: "var(--r5-sage-deep)", color: "#fff",
                  fontFamily: "inherit", fontSize: 15, fontWeight: 600,
                }}
              >
                {pending ? (<><Spinner /> در حال تأیید…</>) : (<><span>تأیید و ورود</span><Icon d="M5 12h14M13 5l7 7-7 7" size={18} stroke={2} /></>)}
              </button>
              <button
                type="button"
                onClick={cancelTwoFactor}
                style={{ background: "none", border: "none", color: "var(--r5-sage-deep)", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
              >
                بازگشت به ورود
              </button>
            </form>
            ) : (
            <form onSubmit={handleLogin} style={{ marginTop: 26, display: "grid", gap: 16 }} noValidate>
              {/* Demo quick-login by role — S-1: ONLY in a VITE_DEMO_MODE build
                  Always shown on the production `nri` tenant (owner directive,
                  S-NRI-1). Clicking a chip fills that role's account EMAIL only;
                  the operator types the password; the actual post-login role
                  still comes from the account server-side. */}
              {tenantSlug.trim().toLowerCase() === "nri" && (
                <DemoRolePicker selected={selectedRole} onPick={pickDemo} />
              )}

              {/* Tenant */}
              <Field
                label="شناسه سازمان / TENANT"
                required
                hint={
                  <span style={{ fontSize: 11, color: "var(--r5-muted)", fontFamily: "var(--r5-mono)" }}>
                    اختیاری برای دانشجوی عمومی
                  </span>
                }
                error={errors.tenantSlug}
              >
                <TextInput
                  name="tenantSlug"
                  type="text"
                  value={tenantSlug}
                  onChange={(e) => {
                    setTenantSlug(e.target.value);
                    setLockUntil(null); // switching account context clears a stale lock countdown
                  }}
                  placeholder="nri"
                  icon="M12 8v8M8 12h8M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2Z"
                />
              </Field>

              {/* Email */}
              <Field label={EMAIL_LABEL} required error={errors.email}>
                <TextInput
                  name="email"
                  type="text"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setLockUntil(null); // switching account clears a stale lock countdown
                  }}
                  placeholder={EMAIL_PLACEHOLDER}
                  dir="ltr"
                  icon="M22 6 12 13 2 6m20 0v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6m20 0a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2"
                />
              </Field>

              {/* Password */}
              <Field
                label="رمز عبور"
                required
                error={errors.password}
                hint={
                  <a
                    href="#forgot"
                    onClick={(e) => {
                      e.preventDefault();
                      go("forgot");
                    }}
                    style={{
                      fontSize: 12,
                      color: "var(--r5-sage-deep)",
                      textDecoration: "none",
                      fontWeight: 500,
                    }}
                  >
                    فراموش کرده‌اید؟
                  </a>
                }
              >
                <TextInput
                  name="password"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••"
                  dir="ltr"
                  icon="M12 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm6-7V8a6 6 0 1 0-12 0v2H4v12h16V10h-2Z"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((s) => !s)}
                  aria-label={showPass ? "مخفی‌سازی رمز" : "نمایش رمز"}
                  style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    insetInlineStart: 4,
                    width: 36,
                    background: "transparent",
                    border: 0,
                    color: "var(--r5-muted)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {showPass ? (
                    <Icon d="M3 3 21 21M10.6 6.1A10 10 0 0 1 12 6c5 0 9 6 9 6a14 14 0 0 1-3.2 3.6M6.6 6.6A14 14 0 0 0 3 12s4 6 9 6a10 10 0 0 0 4.4-1M9.5 9.5a3.5 3.5 0 0 0 5 5" />
                  ) : (
                    <Icon d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Zm10 3a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                  )}
                </button>
                <PassStrength value={password} />
              </Field>

              {/* Toggles row */}
              <div style={{ display: "grid", gap: 10, marginTop: 4 }}>
                <Checkbox checked={remember} onChange={setRemember} label="مرا به خاطر بسپار" />
                <Checkbox
                  checked={twofa}
                  onChange={setTwofa}
                  label="احراز دو‌مرحله‌ای فعال"
                  hint="پس از ورود، کد ۶ رقمی به ایمیل/پیامک شما ارسال می‌شود."
                />
              </div>

              {/* S-2/S-3 wave 2 — persistent lockout countdown (toast vanishes). */}
              {isLocked && (
                <div
                  role="alert"
                  style={{
                    marginTop: 10,
                    padding: "10px 12px",
                    borderRadius: 12,
                    fontSize: 13,
                    lineHeight: 1.7,
                    color: "var(--r5-ink)",
                    background: "color-mix(in oklch, var(--r5-danger, oklch(0.62 0.19 25)) 12%, var(--r5-paper))",
                    border: "1px solid color-mix(in oklch, var(--r5-danger, oklch(0.62 0.19 25)) 35%, transparent)",
                  }}
                >
                  حساب به‌دلیل تلاش‌های ناموفقِ پیاپی موقتاً قفل شده است — {formatLockCountdown(lockRemaining)} تا تلاش دوباره.
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={pending || isLocked}
                style={{
                  marginTop: 10,
                  background: "var(--r5-ink)",
                  color: "var(--r5-paper)",
                  border: 0,
                  borderRadius: 14,
                  padding: "15px 20px",
                  fontSize: 15.5,
                  fontWeight: 600,
                  opacity: isLocked ? 0.55 : 1,
                  cursor: pending ? "wait" : isLocked ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  position: "relative",
                  overflow: "hidden",
                  transition: "transform 160ms ease, box-shadow 200ms ease",
                  boxShadow: "0 8px 24px -8px color-mix(in oklch, var(--r5-ink) 70%, transparent)",
                  minHeight: 48,
                }}
                onMouseEnter={(e) => {
                  if (!pending) e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                }}
              >
                {pending ? (
                  <><Spinner /> در حال ورود …</>
                ) : (
                  <>
                    <span>ورود به حساب</span>
                    <Icon d="M5 12h14M13 5l7 7-7 7" size={18} stroke={2} />
                  </>
                )}
              </button>

              {/* OR divider */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  margin: "8px 0 0",
                  fontSize: 11,
                  color: "var(--r5-muted)",
                  fontFamily: "var(--r5-mono)",
                  letterSpacing: "0.18em",
                }}
              >
                <span style={{ flex: 1, height: 1, background: "var(--r5-line-strong)" }} />
                <span>یا · OR</span>
                <span style={{ flex: 1, height: 1, background: "var(--r5-line-strong)" }} />
              </div>

              {/* SSO */}
              <div className="r5-sso-row" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <SsoButton label="گوگل" glyph={GoogleGlyph} onClick={() => showToast("Google SSO — به‌زودی")} />
                <SsoButton label="درگاه ملی" hint="ID.IR" glyph={NationalGlyph} onClick={() => showToast("ورود از درگاه ملی — به‌زودی")} />
                <SsoButton label="سازمان" hint="SAML / SSO" glyph={OrgGlyph} onClick={() => showToast("ورود سازمانی — به‌زودی")} />
              </div>

              {/* Sign-up CTA — R-AUTH-1: application-only, leads to /apply
                  (open self-registration retired). Appearance unchanged. */}
              <a
                href="/apply"
                onClick={(e) => {
                  e.preventDefault();
                  go("apply");
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 14px",
                  background: "color-mix(in oklch, var(--r5-sage) 14%, var(--r5-paper))",
                  border: "1px dashed color-mix(in oklch, var(--r5-sage) 40%, transparent)",
                  borderRadius: 12,
                  textDecoration: "none",
                  color: "var(--r5-ink-2)",
                  fontSize: 13.5,
                  marginTop: 4,
                  transition: "background 200ms ease",
                }}
              >
                <Icon d="M12 4v16M4 12h16" size={14} stroke={2} />
                <span>
                  حساب کاربری ندارید؟ <b style={{ color: "var(--r5-ink)" }}>همین حالا بسازید →</b>
                </span>
              </a>
            </form>
            )}
          </div>
        </div>

        <CoBrandFooter />
      </section>

      {toastNode}
    </main>
  );
};

export default LoginPage;
